import { keyBy } from "lodash";

import { Customer } from "../Customer/schema";
import { CustomerTab, OrderItem } from "../CustomerTab/schema";
import { sumTransactions } from "../CustomerTab/selectors";
import { BillSplit } from "./schema";

const getOrderItemClaimEntities = (billSplit: BillSplit) =>
  keyBy(billSplit.orderItemClaims, "orderItemId");

const getAllOrderItems = (customerTab: CustomerTab) =>
  customerTab.orders.flatMap((o) => o.orderItems);
export const hasCustomerPaidIn =
  (billSplit: BillSplit) => (customer: Customer) => {
    const customerSession = billSplit.customerSessions.find(
      (cs) => cs.customerId === customer.uuid
    );
    return !!customerSession?.completedAt;
  };
export const getOrderItemCountIn =
  (billSplit: BillSplit, customerTab: CustomerTab) => (customer: Customer) => {
    const claimedOrderItems = getClaimedOrderItemsIn(
      billSplit,
      customerTab
    )(customer);
    return claimedOrderItems.length;
  };

export const getClaimedOrderItemsIn =
  (billSplit: BillSplit, customerTab: CustomerTab) => (customer: Customer) => {
    const orderItems = customerTab.orders.flatMap((o) => o.orderItems) || [];
    const orderItemClaims = getOrderItemClaimEntities(billSplit);
    const claimedOrderItems =
      orderItems.filter(
        (oi) => orderItemClaims[oi.uuid]?.customerId === customer.uuid
      ) || [];
    return claimedOrderItems;
  };

export const getUnclaimedOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab
) => {
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const orderItems = customerTab.orders.flatMap((o) => o.orderItems);
  const unclaimedOrderItems =
    orderItems?.filter((oi) => !orderItemClaims[oi.uuid] && !oi.paymentUuid) ||
    [];
  return unclaimedOrderItems;
};

export const getOrderItemsTotal = (orderItems: OrderItem[]) => {
  return orderItems
    .map((oi) => oi.totalSalesPriceTaxInclusive)
    .reduce((a, b) => a + b, 0);
};

export const getPaidAtTillOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab
) => {
  const onlinePayments = billSplit.customerSessions
    .filter((cs) => cs.paymentUuid)
    .map((cs) => cs.paymentUuid);

  const paidAtTillOrderItems = getAllOrderItems(customerTab)
    .filter((oi) => oi.paymentUuid)
    .filter((oi) => !onlinePayments.includes(oi.paymentUuid));
  return paidAtTillOrderItems;
};

export const getPaidAtTillAmount = (
  billSplit: BillSplit,
  customerTab: CustomerTab
) => {
  const onlinePayments = billSplit.customerSessions
    .filter((cs) => cs.paymentUuid)
    .map((cs) => cs.paymentUuid);

  const paidAtTillAmount = sumTransactions(
    customerTab.transactions.filter((t) => !onlinePayments.includes(t.uuid))
  );
  return paidAtTillAmount;
};

export const getTotalUnassignedTransactionsFor = (
  billSplit: BillSplit,
  customerTab: CustomerTab
) => {
  const orderItems = customerTab.orders.flatMap((o) => o.orderItems);
  const orderItemTransactionsUuids = orderItems
    .filter((oi) => oi.paymentUuid)
    .map((oi) => oi.paymentUuid);
  const transactions = customerTab.transactions.filter(
    (t) => !orderItemTransactionsUuids.includes(t.uuid)
  );
  return sumTransactions(transactions);
};

export const getBillSplitPaidOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab
) => {
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const completedSessionIds = billSplit.customerSessions
    .filter((cs) => !!cs.completedAt)
    .map((cs) => cs.uuid);
  const paidOrderItems = getAllOrderItems(customerTab).filter((oi) =>
    completedSessionIds.includes(orderItemClaims[oi.uuid]?.customerSessionId)
  );
  return paidOrderItems;
};

const isOwner = (billSplit: BillSplit, customer: Customer) => {
  return billSplit.ownerId === customer.uuid;
};
